import _ from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {setRumViewContextProperty} from '../datadog/datadogRum';
import {logCustomActionToDatadog} from '../datadog/profiler';
import {getCache} from './getCache';
import {
  type CachedDataWithSize,
  type CacheTelemetry,
  type LongTermCache,
} from './types';
import {getCacheStorageKey} from './util';

export type UseCachedDataParams<T> = {
  // the compareFunc is used to determine if the data from the cache is the same as the data from the server
  compareFunc?: 'refEquality' | 'deepEquality' | ((a?: T, b?: T) => boolean);
  dataFromServer: T;
  key: any;
  namespace: string;
  waitingForDataFromServer: boolean;
  telemetry?: CacheTelemetry<T>;
};

/**
 * READ BEFORE USING THIS HOOK. SRSLY GUYS.
 * To use this hook properly, there are a couple of things to keep in mind:
 * 1. The caller must ensure that `namespace` is not shared between different queries.
 * 2. When `key == null`, this hook will no-op.
 * 3. When the query changes, the caller must ensure that `key` changes either at the same time or BEFORE `dataFromServer` changes.
 *    Otherwise, we will cache the new data under the old key, so the old key will have invalid data.
 */
export function useCachedData<T>(
  {
    compareFunc,
    dataFromServer,
    key,
    namespace,
    waitingForDataFromServer,
    telemetry,
  }: UseCachedDataParams<T>,
  skip: boolean = false
): T {
  return useCachedDataWithOrigin(
    {
      compareFunc,
      dataFromServer,
      key,
      namespace,
      waitingForDataFromServer,
      telemetry,
    },
    skip
  ).data;
}

const DEFAULT_COMPARE_FUNCS = {
  refEquality: (a: any, b: any) => a === b,
  deepEquality: (a: any, b: any) => _.isEqual(a, b),
};

export function useCachedDataWithOrigin<T>(
  {
    compareFunc = 'refEquality',
    dataFromServer,
    key,
    namespace,
    waitingForDataFromServer,
    telemetry,
  }: UseCachedDataParams<T>,
  skip: boolean = false
): {data: T; origin: 'cache' | 'server'} {
  /**
   * The isMounted variable is used here as a flag to prevent calling setCachedState on an unmounted component. This is necessary because the cache.get call is asynchronous, and the component may unmount before it completes. By setting isMounted to false in the cleanup function, we ensure that setCachedState is only called if the component is still mounted, which helps avoid React warnings and potential memory leaks.
   */
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const readiness = useRef<CacheReadiness<T> | null>(null);

  const cache = getCache();

  const internalKey = useMemo(
    () => (skip ? null : getCacheStorageKey(namespace, key)),
    [namespace, key, skip]
  );

  // State holds both the current internalKey and the cache data for it
  const [cachedState, setCachedState] = useState<{
    internalKey: string | null;
    data: CachedDataWithSize<T> | null;
  }>({internalKey, data: null});

  const readCacheAndUpdateState = useCallback(
    async function readCacheAndUpdateState(
      internalKey: string | null,
      cache: LongTermCache,
      skip: boolean,
      namespace: string,
      attempt: CacheReadiness<T>,
      telemetry?: CacheTelemetry<T>
    ) {
      if (!internalKey || !cache || skip) {
        return;
      }

      const fromCache = telemetry
        ? await cache.get<T>(internalKey, namespace, telemetry)
        : await cache.get<T>(internalKey, namespace);
      if (readiness.current === attempt && isMounted.current) {
        attempt.cacheReadMs = performance.now() - attempt.start;
        attempt.cacheHit = fromCache != null;
        attempt.cachedData = fromCache?.data;
        recordReadiness(attempt);
      }
      if (fromCache && isMounted.current) {
        // Only fill empty state. If the server answered before this read
        // resolved, its data is already in state and is newer than the cache.
        setCachedState(prevState =>
          prevState.internalKey === internalKey && prevState.data == null
            ? {internalKey, data: fromCache}
            : prevState
        );
      }
    },
    []
  );

  /**
   * If the internalKey changes, immediately reset the cached data so that we don't return stale data while we're waiting for new data to be read from the cache or provided by the server
   */
  useEffect(() => {
    if (skip) {
      return;
    }
    setCachedState({internalKey, data: null});
  }, [internalKey, skip]);

  /**
   * Read Cache and Update State:
   * The only times we want to read from the cache and update the internal state of this hook are:
   * 1. When `skip` changes: as true => false means we want to start receiving updates
   * 2. When the `cache` changes value, e.g. if we loaded a cache or switched them out
   *   - I think it's possible that IF we switch caches after receiving a server response we wouldn't want to update the state from the switch-in cache given that it's likely more stale. However since right now we only have one cache backing this hook we don't need to worry about that.
   * 3. If the internal key changes we need to read the cache and update the state
   */
  useEffect(
    function readCacheOnDependencyChange() {
      if (skip || !internalKey) {
        readiness.current = null;
        return;
      }
      const attempt: CacheReadiness<T> = {
        namespace,
        internalKey,
        context: telemetry?.context,
        start: performance.now(),
        cacheReadMs: null,
        serverReadyMs: null,
        cacheHit: false,
        cacheUsedBeforeServer: false,
        logged: false,
      };
      readiness.current = attempt;
      readCacheAndUpdateState(
        internalKey,
        cache,
        skip,
        namespace,
        attempt,
        telemetry
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [internalKey, cache, skip, namespace]
  );

  /**
   * When we get fresh data from the server, we need to:
   * 1) Update the state with the new data
   * 2) Syndicate that updated data to the cache
   *
   * We do not want to fire this when the internalKey changes, as we have to wait until `dataFromServer` updates to the new query result before we want to store it under the new `internalKey`. We handle nulling out the immediate value of this hook's state directly in the `useEffect` that handles the `internalKey` change.
   * Loading completion must also trigger this effect: a placeholder can have
   * the same reference as the final server data while cached state is stale.
   */
  useEffect(
    function cacheFreshServerData() {
      if (
        skip || // skip the cache when skip is true
        waitingForDataFromServer || // skip when still waiting for data
        !internalKey || // skip the cache when we don't have a key to write under
        !cache // skip the cache when no cache is available
      ) {
        return;
      }

      /**
       * We do the equality check here as a safeguard against thrashing renders in the UI if the server is returning the same data over and over again (e.g. polling against unchanging data). For delta queries, the reference should be held stable higher up, so this deep equality check is not needed. However, the cost of the deep equality check is trivial compared to the render hit of certain components like line plots, so we'd rather be cautious and do the check for all hits on this hook.
       */
      if (
        typeof compareFunc === 'function'
          ? !compareFunc(cachedState.data?.data, dataFromServer)
          : !DEFAULT_COMPARE_FUNCS[compareFunc](
              cachedState.data?.data,
              dataFromServer
            )
      ) {
        // Update our state with the new data
        setCachedState(prevState =>
          prevState.internalKey === internalKey
            ? {internalKey, data: {data: dataFromServer, bytesReadFromCache: 0}}
            : prevState
        );

        // Store in cache
        if (telemetry) {
          cache.set(internalKey, dataFromServer, namespace, telemetry);
        } else {
          cache.set(internalKey, dataFromServer, namespace);
        }
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [dataFromServer, waitingForDataFromServer]
  );

  // Always return stable cached state
  const data = skip
    ? dataFromServer
    : (cachedState.data?.data ?? dataFromServer);
  const bytesReadFromCache = cachedState.data?.bytesReadFromCache ?? 0;
  const origin: 'cache' | 'server' =
    cachedState.data && bytesReadFromCache > 0 ? 'cache' : 'server';

  useEffect(() => {
    const attempt = readiness.current;
    if (skip || !attempt || attempt.internalKey !== internalKey) {
      return;
    }
    if (
      waitingForDataFromServer &&
      origin === 'cache' &&
      data === attempt.cachedData
    ) {
      try {
        if (telemetry?.isUsable?.(data) ?? true) {
          attempt.cacheUsedBeforeServer = true;
        }
      } catch {
        // Optional telemetry must not affect rendering.
      }
    }
    if (
      !waitingForDataFromServer &&
      dataFromServer != null &&
      attempt.serverReadyMs == null
    ) {
      attempt.serverReadyMs = performance.now() - attempt.start;
    }
    recordReadiness(attempt);
  }, [
    data,
    dataFromServer,
    internalKey,
    origin,
    skip,
    telemetry,
    waitingForDataFromServer,
  ]);

  // The initial server placeholder must not consume the first-hydration measurement.
  useSetBytesReadFromCache(
    namespace,
    origin === 'cache' ? data : null,
    bytesReadFromCache,
    skip
  );

  return {
    data,
    origin: skip ? 'server' : origin,
  };
}

type CacheReadiness<T> = {
  namespace: string;
  internalKey: string;
  context?: CacheTelemetry<T>['context'];
  start: number;
  cacheReadMs: number | null;
  serverReadyMs: number | null;
  cachedData?: T;
  cacheHit: boolean;
  cacheUsedBeforeServer: boolean;
  logged: boolean;
};

function recordReadiness<T>(attempt: CacheReadiness<T>) {
  if (
    attempt.logged ||
    attempt.cacheReadMs == null ||
    attempt.serverReadyMs == null
  ) {
    return;
  }
  attempt.logged = true;
  attempt.cachedData = undefined;
  try {
    logCustomActionToDatadog('cachehook.readiness', {
      ...attempt.context,
      namespace: attempt.namespace,
      cacheReadMs: attempt.cacheReadMs,
      serverReadyMs: attempt.serverReadyMs,
      cacheHit: attempt.cacheHit,
      cacheUsedBeforeServer: attempt.cacheUsedBeforeServer,
      serverReadyMinusCacheReadMs: attempt.serverReadyMs - attempt.cacheReadMs,
    });
  } catch {
    // A telemetry failure must not interrupt cache hydration.
  }
}

export function useSetBytesReadFromCache(
  namespace: string,
  result: any | null,
  bytesReadFromCache: number,
  skip: boolean
) {
  const haveSeenData = useRef(false);
  useEffect(() => {
    if (skip) {
      return;
    }
    if (result != null && !haveSeenData.current) {
      haveSeenData.current = true;
      setRumViewContextProperty(
        `${namespace}BytesReadFromCache`,
        bytesReadFromCache
      );
    }
  }, [result, bytesReadFromCache, namespace, skip]);
}
